import { ModuleRendering, SlotRendering, getModuleSpotLayoutMode, isSlotRenderingRenderable } from '@frontend/common-types';
import React from 'react';

import { Slot } from '../slot/slot.component';
import { useModuleController } from './module.controller';

interface ModuleProps<TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering> {
    module: TModule;
    scale: number;
    offsetX: number;
    offsetY: number;
    onSlotClick?: (clickedSlot: TSlot) => void;
    showLabels?: boolean;
    onModuleClick?: (module: TModule) => void;
    spotId?: string;
    onModuleExpand?: (spotId: string, moduleId: string) => void;
    isExpanded?: boolean;
    enableSlotHover?: boolean;
    slotHoverElement?: (slot: TSlot) => React.ReactNode;
    getSlotColor?: (slot: TSlot) => string;
    slotRenderElement?: (slot: TSlot) => React.ReactNode;
}

export const Module = <TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering>(props: ModuleProps<TModule, TSlot>) => {
    const {
        module,
        scale,
        offsetX,
        offsetY,
        onSlotClick,
        showLabels = true,
        onModuleClick,
        onModuleExpand,
        isExpanded = false,
        enableSlotHover = true,
        slotHoverElement,
        getSlotColor,
        slotRenderElement
    } = props;
    const {
        isCarousel,
        doorCount,
        handleModuleClick,
        handleCarouselClick,
        handleLabelClick,
        moduleLeft,
        moduleWidth,
        moduleHeight,
        moduleTop,
        doorWidth,
        doorHeight,
        doorColumnLeft
    } = useModuleController(module, scale, offsetX, offsetY, onModuleClick, onModuleExpand);

    // Non-renderable → SpotLayout owns ModuleSlotList. Expanded renderable → geometric slots only.
    const layoutMode = getModuleSpotLayoutMode(module, { isExpanded, whenNotRenderable: 'slot-list' });

    if (layoutMode === 'slot-list') {
        return null;
    }

    const renderableSlots = module.slots?.filter(isSlotRenderingRenderable) ?? [];

    if (layoutMode === 'slots-only') {
        return (
            <>
                {renderableSlots.map((slot) => (
                    <Slot
                        key={slot.id}
                        slot={slot as TSlot}
                        scale={scale}
                        offsetX={offsetX}
                        offsetY={offsetY}
                        onSlotClick={onSlotClick}
                        showLabels={showLabels}
                        enableHover={enableSlotHover}
                        hoverElement={slotHoverElement}
                        getSlotColor={getSlotColor}
                        renderElement={slotRenderElement}
                    />
                ))}
            </>
        );
    }

    return (
        <>
            {isCarousel ? (
                <>
                    <div
                        data-testid='module-box'
                        className='bg-brand-secondary absolute flex cursor-pointer items-center justify-center'
                        style={{
                            left: `${moduleLeft}px`,
                            top: `${moduleTop}px`,
                            width: `${moduleWidth}px`,
                            height: `${moduleHeight}px`
                        }}
                        onClick={handleCarouselClick}
                    />

                    {/* Carousel door sections container */}
                    <div
                        className='absolute flex h-72 flex-col gap-1'
                        style={{
                            left: `${doorColumnLeft}px`,
                            top: `${moduleTop + (moduleHeight - 288) / 2}px`
                        }}>
                        {/* Render door sections - centered within module */}
                        {Array.from({ length: doorCount }).map((_, index) => {
                            return (
                                <div
                                    key={`door-${index}`}
                                    className='border-background-default flex items-end border-2'
                                    style={{
                                        width: `${doorWidth}px`,
                                        height: `${doorHeight}px`,
                                        padding: `${8 * scale}px`
                                    }}>
                                    <span className='text-background-surface text-sm font-semibold'>{index + 1}</span>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div
                    data-testid='module-box'
                    className='bg-background-surface absolute rounded'
                    style={{
                        left: `${moduleLeft}px`,
                        top: `${moduleTop}px`,
                        width: `${moduleWidth}px`,
                        height: `${moduleHeight}px`,
                        cursor: onModuleClick ? 'pointer' : 'default'
                    }}
                    onClick={handleModuleClick}
                />
            )}

            {renderableSlots.map((slot) => (
                <Slot
                    key={slot.id}
                    slot={slot as TSlot}
                    scale={scale}
                    offsetX={offsetX}
                    offsetY={offsetY}
                    onSlotClick={onSlotClick}
                    showLabels={showLabels}
                    enableHover={enableSlotHover}
                    hoverElement={slotHoverElement}
                    getSlotColor={getSlotColor}
                    renderElement={slotRenderElement}
                />
            ))}

            {/* Module label - positioned above the module, centered - rendered last for proper z-index */}
            {showLabels && (
                <div
                    className={`absolute ${!isCarousel && onModuleExpand ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none'}`}
                    style={{
                        left: `${moduleLeft + moduleWidth / 2}px`,
                        top: `${moduleTop - 45}px`,
                        transform: 'translateX(-50%)',
                        zIndex: 1000
                    }}
                    onClick={!isCarousel && onModuleExpand ? handleLabelClick : undefined}>
                    <span className='text-foreground-default text-base font-semibold whitespace-nowrap'>{module.index}</span>
                </div>
            )}
        </>
    );
};
